// Notepad — lightweight sticky notes, browser localStorage only. No server,
// no sync. Deliberately throwaway: anything that belongs to a customer should
// be logged as an activity instead.
import { useEffect, useState } from "react";
import { Plus, X, Archive } from "lucide-react";

const KEY = "t2_notes";
const COLOURS = [
  { k: "yellow", cls: "bg-yellow-500/15 border-yellow-500/40" },
  { k: "teal", cls: "bg-teal-500/15 border-teal-500/40" },
  { k: "orange", cls: "bg-orange-500/15 border-orange-500/40" },
  { k: "rose", cls: "bg-rose-500/15 border-rose-500/40" },
  { k: "slate", cls: "bg-slate-500/15 border-slate-500/40" },
];
const colCls = (k) => (COLOURS.find((c) => c.k === k) || COLOURS[0]).cls;
const load = () => { try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; } };

export default function Notepad() {
  const [notes, setNotes] = useState(load);
  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(notes));
    window.dispatchEvent(new Event("t2-notes-changed"));
  }, [notes]);
  // Reflect edits made in the floating quick-notes panel (same browser).
  // Guard: only update when content actually changed, else the save-effect
  // above would re-fire and loop.
  useEffect(() => {
    const reload = () => setNotes((prev) => {
      const next = load();
      return JSON.stringify(next) === JSON.stringify(prev) ? prev : next;
    });
    window.addEventListener("t2-notes-changed", reload);
    window.addEventListener("storage", reload);
    return () => { window.removeEventListener("t2-notes-changed", reload); window.removeEventListener("storage", reload); };
  }, []);

  const add = () => setNotes((n) => [{ id: Date.now().toString(36), title: "", body: "", colour: "yellow" }, ...n]);
  const patch = (id, p) => setNotes((n) => n.map((x) => (x.id === id ? { ...x, ...p } : x)));
  const del = (id) => setNotes((n) => n.filter((x) => x.id !== id));

  return (
    <div data-testid="notepad">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] text-slate-500 font-mono">Saved in this browser only · {notes.length} note{notes.length === 1 ? "" : "s"}</span>
        <button onClick={add} data-testid="notepad-add" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-teal-400/40 bg-teal-500/10 text-teal-200 text-[11.5px]">
          <Plus className="w-3.5 h-3.5" /> Add note
        </button>
      </div>
      {notes.length === 0 ? (
        <div className="text-center py-6 text-[12.5px] text-slate-500">No notes yet. Jot a quick scratch — it stays on this device.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2 gap-3">
          {notes.map((note) => (
            <div key={note.id} className={`rounded-lg border p-2.5 ${colCls(note.colour)}`} data-testid={`note-${note.id}`}>
              <div className="flex items-center gap-1 mb-1.5">
                <input value={note.title} onChange={(e) => patch(note.id, { title: e.target.value })} placeholder="Title"
                  className="flex-1 bg-transparent outline-none text-[14px] font-semibold text-slate-100 placeholder:text-slate-500" />
                <button onClick={() => del(note.id)} data-testid={`note-del-${note.id}`} className="p-0.5 text-slate-400 hover:text-rose-300"><X className="w-3.5 h-3.5" /></button>
              </div>
              <textarea value={note.body} onChange={(e) => patch(note.id, { body: e.target.value })} placeholder="Jot something…" rows={8}
                className="w-full bg-transparent outline-none resize-y min-h-[150px] text-[13.5px] leading-relaxed text-slate-200 placeholder:text-slate-500" />
              <div className="flex items-center gap-1.5 mt-1.5">
                {COLOURS.map((c) => (
                  <button key={c.k} onClick={() => patch(note.id, { colour: c.k })} title={c.k}
                    className={`w-3.5 h-3.5 rounded-full border ${c.cls} ${note.colour === c.k ? "ring-1 ring-white/60" : ""}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
